import React, {useContext, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar,} from '@mui/material';
import {TaskStatus} from '../../../../services/openapi/models/TaskStatus';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {Task} from '../../../../services/openapi/models/Task';
import type {TaskUpdate} from '../../../../services/openapi/models/TaskUpdate';
import {TaskImage} from '../../../../services/openapi/models/TaskImage';
import {TaskDocument} from '../../../../services/openapi/models/TaskDocument';
import {useTaskDetail} from './hooks/useTaskDetail';
import {TaskHeader, TaskTitle} from './components/TaskHeader';
import {TaskStatusDetails} from './components/TaskStatusDetails';
import {TaskContacts} from './components/TaskContacts';
import {TimeTracking} from './components/TimeTracking';
import {TaskDescription} from './components/TaskDescription';
import {TaskAttachments} from './components/TaskAttachments';
import {EmailDocumentModal} from './components/EmailDocumentModal';
import {getDocumentIcon, getStatusColor, getStatusIcon, getStatusLabel,} from './utils/taskUtils';
import {createTaskApiHandlers} from './utils/taskApiHandlers';
import {createRapport} from './utils/rapportGenerator';
import {TaskServiceUtils} from '../../../../services/TaskServiceUtils';
import ImageCrop from './task-image-crop';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {ArchiveTaskModal, CompleteTaskModal, ImageEditModal, ImagePreviewModal, PermanentDeleteModal, RapportCompleteModal, StatusChangeModal, UnsavedChangesModal,} from './Modals';
import PermanentDeleteModalFile from './Modals/PermanentDeleteModalFile';
import {InvoiceEmitter} from '../../../../services/invoiceService';
import EmitterSelectDialog from '../RapporteRechnung/EmitterSelectDialog';
import InvoiceCreateDialog, {TaskDataForInvoice} from '../RapporteRechnung/InvoiceCreateDialog';

// Extended TaskUpdate to include new contact fields (until OpenAPI types are regenerated)
interface ExtendedTaskUpdate extends TaskUpdate {
    projekt_name?: string;
    auftraggeber_street?: string;
    auftraggeber_additional_street?: string;
    auftraggeber_postal_code?: string;
    auftraggeber_city?: string;
    appointment_name?: string;
    appointment_email?: string;
    appointment_phone?: string;
    appointment_street?: string;
    appointment_additional_street?: string;
    appointment_postal_code?: string;
    appointment_city?: string;
    invoice_name?: string;
    invoice_email?: string;
    invoice_phone?: string;
    invoice_street?: string;
    invoice_additional_street?: string;
    invoice_postal_code?: string;
    invoice_city?: string;
}

interface TaskDetailProps {
    taskId: number;
    isOpen: boolean;
    onClose: () => void;
    onTaskUpdated?: () => void;
}

export default function TaskDetail({taskId, isOpen, onClose, onTaskUpdated}: TaskDetailProps) {
    const {call_backend_api} = useContext(APIWrapperContext);
    const {user_roll} = useContext(UserContext);

    const {
        task,
        setTask,
        loading,
        error,
        users,
        bau,
        contacts,
        uploading,
        setUploading,
        fileInputRef,
        currentUserId,
        sessionUserId,
        isAdmin,
        isTaskArchived,
        isTaskCreator,
        getCurrentTimeFrom,
        getCurrentTimeTo,
        getTotalDuration,
        formatDate,
        wtFrom,
        setWtFrom,
        wtTo,
        setWtTo,
    } = useTaskDetail(taskId, isOpen, onTaskUpdated);

    const [editMode, setEditMode] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<{ [key: string]: any }>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [editingField, setEditingField] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<{ [key: string]: any }>({});
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
    const [assignmentMenuAnchor, setAssignmentMenuAnchor] = useState<null | HTMLElement>(null);
    const [bauvorhabenMenuAnchor, setBauvorhabenMenuAnchor] = useState<null | HTMLElement>(null);

    const [openEndTask, setOpenEndTask] = useState(false);
    const [openRapportComplete, setOpenRapportComplete] = useState(false);
    const [openDeleteTask, setOpenDeleteTask] = useState(false);
    const [openPermanentDeleteTask, setOpenPermanentDeleteTask] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [permanentDeleting, setPermanentDeleting] = useState(false);
    const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState(false);
    const [imageMenuAnchor, setImageMenuAnchor] = useState<{ [key: number]: HTMLElement | null }>({});
    const [documentMenuAnchor, setDocumentMenuAnchor] = useState<{ [key: number]: HTMLElement | null }>(
        {}
    );

    const [openPermanentDeleteFile, setOpenPermanentDeleteFile] = useState(false);
    const [openDeleteFileModal, setOpenDeleteFileModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<{ id: number, type: 'image' | 'document', name?: string, item?: TaskImage | TaskDocument } | null>(null);

    const [openEmailModal, setOpenEmailModal] = useState(false);
    const [documentToEmail, setDocumentToEmail] = useState<TaskDocument | null>(null);

    const [selectedImage, setSelectedImage] = useState<TaskImage | null>(null);
    const [editingImageId, setEditingImageId] = useState<number | null>(null);
    const [editingImageFile, setEditingImageFile] = useState<File | null>(null);
    const [croppingImageId, setCroppingImageId] = useState<number | null>(null);
    const [croppingImageFile, setCroppingImageFile] = useState<File | null>(null);

    const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);

    // Invoice creation state
    const [emitterSelectOpen, setEmitterSelectOpen] = useState(false);
    const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
    const [selectedEmitter, setSelectedEmitter] = useState<InvoiceEmitter | null>(null);
    const [taskDataForInvoice, setTaskDataForInvoice] = useState<TaskDataForInvoice | undefined>(undefined);
    const [selectedRapportDocument, setSelectedRapportDocument] = useState<TaskDocument | null>(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'error' | 'warning' | 'info'
    >('success');
    const [snackbarLoading, setSnackbarLoading] = useState(false);

    const showSnackbar = (
        message: string,
        severity: 'success' | 'error' | 'warning' | 'info' = 'success',
        loading: boolean = false
    ) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarLoading(loading);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        // Don't close if loading or if clicking away
        if (snackbarLoading || reason === 'clickaway') return;
        setSnackbarOpen(false);
        setSnackbarLoading(false);
    };

    const checkAndUpdateStatusToInProgressWithTask = async (taskData: Task) => {
        try {
            if (!sessionUserId) {
                return false;
            }

            const isAssignedUser = taskData.assigned_to === sessionUserId;
            const isStatusOpen = taskData.status === TaskStatus.OPEN;

            if (isAssignedUser && isStatusOpen) {
                setOpenStatusChangeModal(true);
                return true;
            }

            return false;
        } catch (error) {
            showSnackbar('Fehler bei Status-Prüfung', 'error');
            return false;
        }
    };

    const performStatusChangeToInProgress = async () => {
        try {
            if (!task) return;

            call_backend_api(
                TasksService.updateTaskTaskTaskIdPut(task.id, {status: TaskStatus.IN_PROGRESS}),
                (updatedTask: Task) => {
                    setTask({
                        ...updatedTask,
                        images: task?.images || [],
                        documents: task?.documents || [],
                    });
                    showSnackbar('Status erfolgreich auf "In Bearbeitung" gesetzt', 'success');
                    if (onTaskUpdated) onTaskUpdated();
                },
                (error: any) => {
                    console.error('Error updating task status:', error);
                    showSnackbar('Fehler beim Aktualisieren des Status', 'error');
                },
                undefined,
                false
            );
        } catch (error) {
            console.error('Error updating task status:', error);
            showSnackbar('Fehler beim Aktualisieren des Status', 'error');
        }
    };

    const performClose = () => {
        setPendingChanges({});
        setHasUnsavedChanges(false);
        setEditMode(false);
        setEditingField(null);
        setEditValues({});

        onClose();
    };

    const handleStatusChangeConfirm = async () => {
        await performStatusChangeToInProgress();
        setOpenStatusChangeModal(false);
        performClose();
    };

    const handleStatusChangeCancel = () => {
        setOpenStatusChangeModal(false);
        performClose();
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !task) return;

        setUploading(true);

        try {
            for (const file of Array.from(files)) {
                const isImage = file.type.startsWith('image/');

                if (isImage) {
                    await new Promise<void>((resolve, reject) => {
                        call_backend_api(
                            TasksService.uploadTaskImageTaskTaskIdImagesPost(taskId, {file}),
                            (uploadedImage: TaskImage) => {
                                console.log('Image uploaded successfully:', uploadedImage);
                                if (task) {
                                    setTask({
                                        ...task,
                                        images: [...(task.images || []), uploadedImage],
                                    });
                                }
                                showSnackbar('Bild erfolgreich hochgeladen', 'success');
                                resolve();
                            },
                            (error: any) => {
                                console.error('Image upload error:', error);
                                showSnackbar(`Fehler beim Hochladen von ${file.name}`, 'error');
                                reject(error);
                            },
                            undefined,
                            false
                        );
                    });
                } else {
                    await new Promise<void>((resolve, reject) => {
                        call_backend_api(
                            TasksService.uploadTaskDocumentTaskTaskIdDocumentsPost(taskId, {file}),
                            (uploadedDocument: TaskDocument) => {
                                console.log('Document uploaded successfully:', uploadedDocument);
                                if (task) {
                                    setTask({
                                        ...task,
                                        documents: [...(task.documents || []), uploadedDocument],
                                    });
                                }
                                showSnackbar('Dokument erfolgreich hochgeladen', 'success');
                                resolve();
                            },
                            (error: any) => {
                                console.error('Document upload error:', error);
                                showSnackbar(`Fehler beim Hochladen von ${file.name}`, 'error');
                                reject(error);
                            },
                            undefined,
                            false
                        );
                    });
                }
            }
        } catch (err) {
            console.error('Error uploading files:', err);
            showSnackbar('Fehler beim Hochladen der Dateien', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleImageClick = (image: TaskImage) => {
        setSelectedImage(image);
    };

    const handleEditImage = async (image: TaskImage) => {
        try {
            console.log('Starting image edit for image:', image);

            const base64Images = await TasksService.getTaskImagesBase64TaskTaskIdImagesBase64Get(
                taskId,
                image.id.toString()
            );

            if (!base64Images || base64Images.length === 0) {
                throw new Error('Bild nicht gefunden');
            }

            const base64Image = base64Images[0];
            console.log('Base64 image response:', base64Image);
            console.log('Base64 image loaded, data_url length:', base64Image.data_url?.length);

            if (!base64Image.data_url) {
                throw new Error('Keine Bilddaten erhalten');
            }

            const base64Data = base64Image.data_url.split(',')[1];
            const mimeType = base64Image.data_url.split(',')[0].split(':')[1].split(';')[0];

            console.log('MIME type detected:', mimeType);

            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);

            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], {type: mimeType});
            console.log('Image blob created:', blob.size, 'bytes, type:', blob.type);

            const file = new File([blob], `image-${image.id}.${mimeType.split('/')[1]}`, {
                type: mimeType,
                lastModified: Date.now()
            });
            console.log('File created:', file.name, file.size, 'bytes, type:', file.type);

            setEditingImageId(image.id);
            setEditingImageFile(file);

            console.log('Edit modal should open now');
        } catch (error) {
            console.error('Error loading image for editing:', error);
            showSnackbar('Fehler beim Laden des Bildes zum Bearbeiten', 'error');
        }
    };

    const handleCropImage = async (image: TaskImage) => {
        try {
            const imageUrl = `${
                import.meta.env.DEV
                    ? 'http://localhost:8008'
                    : import.meta.env.VITE_BACKEND_URL || ''
            }${image.image_path}`;

            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const file = new File([blob], `image-${image.id}.jpg`, {type: blob.type});

            setCroppingImageId(image.id);
            setCroppingImageFile(file);
        } catch (error) {
            console.error('Error loading image for cropping:', error);
            showSnackbar('Fehler beim Laden des Bildes', 'error');
        }
    };

    const handleSaveEditedImage = async (editedBlob: Blob) => {
        if (editingImageId === null) return;

        try {
            const editedFile = new File([editedBlob], `edited-image-${editingImageId}.png`, {
                type: 'image/png',
            });

            await call_backend_api(
                TasksService.deleteTaskImageTaskTaskIdImagesImageIdDelete(taskId, editingImageId),
                async () => {
                    await call_backend_api(
                        TasksService.uploadTaskImageTaskTaskIdImagesPost(taskId, {file: editedFile}),
                        (uploadedImage: TaskImage) => {
                            if (task) {
                                setTask({
                                    ...task,
                                    images: task.images?.map((img) =>
                                        img.id === editingImageId ? uploadedImage : img
                                    ) || [uploadedImage],
                                });
                            }
                            showSnackbar('Bild erfolgreich bearbeitet', 'success');
                        },
                        (error: any) => {
                            console.error('Error uploading edited image:', error);
                            showSnackbar('Fehler beim Hochladen des bearbeiteten Bildes', 'error');
                        },
                        undefined,
                        false
                    );
                },
                (error: any) => {
                    console.error('Error deleting original image:', error);
                    showSnackbar('Fehler beim Löschen des ursprünglichen Bildes', 'error');
                },
                undefined,
                false
            );

            setEditingImageId(null);
            setEditingImageFile(null);
        } catch (error) {
            console.error('Error saving edited image:', error);
            showSnackbar('Fehler beim Speichern des bearbeiteten Bildes', 'error');
        }
    };

    const handleSaveCroppedImage = async (croppedBlob: Blob) => {
        if (croppingImageId === null) return;

        try {
            const croppedFile = new File([croppedBlob], `cropped-image-${croppingImageId}.png`, {
                type: 'image/png',
            });

            await call_backend_api(
                TasksService.deleteTaskImageTaskTaskIdImagesImageIdDelete(taskId, croppingImageId),
                async () => {
                    await call_backend_api(
                        TasksService.uploadTaskImageTaskTaskIdImagesPost(taskId, {file: croppedFile}),
                        (uploadedImage: TaskImage) => {
                            if (task) {
                                setTask({
                                    ...task,
                                    images: task.images?.map((img) =>
                                        img.id === croppingImageId ? uploadedImage : img
                                    ) || [uploadedImage],
                                });
                            }
                            showSnackbar('Bild erfolgreich zugeschnitten', 'success');
                        },
                        (error: any) => {
                            console.error('Error uploading cropped image:', error);
                            showSnackbar('Fehler beim Hochladen des zugeschnittenen Bildes', 'error');
                        },
                        undefined,
                        false
                    );
                },
                (error: any) => {
                    console.error('Error deleting original image:', error);
                    showSnackbar('Fehler beim Löschen des ursprünglichen Bildes', 'error');
                },
                undefined,
                false
            );

            setCroppingImageId(null);
            setCroppingImageFile(null);
        } catch (error) {
            console.error('Error saving cropped image:', error);
            showSnackbar('Fehler beim Speichern des zugeschnittenen Bildes', 'error');
        }
    };

    const handleDownloadImage = async (image: TaskImage) => {
        try {
            const imageUrl = `${
                import.meta.env.DEV
                    ? 'http://localhost:8008'
                    : import.meta.env.VITE_BACKEND_URL || ''
            }${image.image_path}`;

            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `task-${taskId}-image-${image.id}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSnackbar('Bild erfolgreich heruntergeladen', 'success');
        } catch (error) {
            console.error('Download image error:', error);
            showSnackbar('Fehler beim Herunterladen des Bildes', 'error');
        }
    };

    const handleDeleteImage = (imageId: number) => {
        if (!task) return;

        const image = task.images?.find(img => img.id === imageId);
        setFileToDelete({
            id: imageId,
            type: 'image',
            name: image ? `Bild ${imageId}` : `Bild ${imageId}`
        });
        setOpenDeleteFileModal(true);
    };

    const handleDeleteDocument = (documentId: number) => {
        if (!task) return;

        const document = task.documents?.find(doc => doc.id === documentId);
        setFileToDelete({
            id: documentId,
            type: 'document',
            name: document ? document.filename : `Dokument ${documentId}`
        });
        setOpenDeleteFileModal(true);
    };

    const confirmDeleteFile = () => {
        if (!fileToDelete) return;

        if (fileToDelete.type === 'image') {
            call_backend_api(
                TasksService.deleteTaskImageTaskTaskIdImagesImageIdDelete(taskId, fileToDelete.id),
                () => {
                    if (task) {
                        setTask({
                            ...task,
                            images: task.images?.filter((img: TaskImage) => img.id !== fileToDelete.id),
                        });
                    }
                    showSnackbar('Bild erfolgreich gelöscht', 'success');
                },
                (error: any) => {
                    console.error('Delete image error:', error);
                    showSnackbar('Fehler beim Löschen des Bildes', 'error');
                },
                undefined,
                false
            );
        } else if (fileToDelete.type === 'document') {
            call_backend_api(
                TasksService.deleteTaskDocumentTaskTaskIdDocumentsDocumentIdDelete(taskId, fileToDelete.id),
                () => {
                    if (task) {
                        setTask({
                            ...task,
                            documents: task.documents?.filter((doc: TaskDocument) => doc.id !== fileToDelete.id),
                        });
                    }
                    showSnackbar('Dokument erfolgreich gelöscht', 'success');
                },
                (error: any) => {
                    console.error('Delete document error:', error);
                    showSnackbar('Fehler beim Löschen des Dokuments', 'error');
                },
                undefined,
                false
            );
        }

        setOpenDeleteFileModal(false);
        setFileToDelete(null);
    };

    const handleDownloadDocument = async (doc: TaskDocument) => {
        try {
            const documentUrl = `${
                import.meta.env.DEV
                    ? 'http://localhost:8008'
                    : import.meta.env.VITE_BACKEND_URL || ''
            }${(doc as any).document_path}`;

            const response = await fetch(documentUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = doc.filename;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSnackbar('Dokument erfolgreich heruntergeladen', 'success');
        } catch (error) {
            console.error('Error downloading document:', error);
            showSnackbar('Fehler beim Herunterladen des Dokuments', 'error');
        }
    };

    const handleShareDocument = async (doc: TaskDocument) => {
        setDocumentToEmail(doc);
        setOpenEmailModal(true);
    };

    // Invoice creation handlers
    const handleCreateInvoiceFromRapport = (document: TaskDocument) => {
        if (!task) return;

        setSelectedRapportDocument(document);

        // Extract invoice-relevant data from current task
        setTaskDataForInvoice({
            invoice_name: (task as any).invoice_name,
            invoice_email: (task as any).invoice_email,
            invoice_phone: (task as any).invoice_phone,
            invoice_street: (task as any).invoice_street,
            invoice_additional_street: (task as any).invoice_additional_street,
            invoice_postal_code: (task as any).invoice_postal_code,
            invoice_city: (task as any).invoice_city,
            invoice_full_contact: (task as any).invoice_full_contact,
            invoice_use_full_contact: (task as any).invoice_use_full_contact,
            arbeitszeit: (task as any).arbeitszeit,
            anfahrt_abfahrt_mit_kfz: (task as any).anfahrt_abfahrt_mit_kfz,
            material: (task as any).material,
            title: task.title,
            description: task.description,
            rapport: (task as any).rapport,
            projekt_name: (task as any).projekt_name,
            workDate: document.uploaded_at,
        });
        setEmitterSelectOpen(true);
    };

    const handleEmitterSelect = (emitter: InvoiceEmitter) => {
        setSelectedEmitter(emitter);
        setEmitterSelectOpen(false);
        setInvoiceCreateOpen(true);
    };

    const handleInvoiceClose = () => {
        setInvoiceCreateOpen(false);
        setSelectedEmitter(null);
        setSelectedRapportDocument(null);
        setTaskDataForInvoice(undefined);
    };

    const handleInvoiceSave = () => {
        setInvoiceCreateOpen(false);
        setSelectedEmitter(null);
        setSelectedRapportDocument(null);
        setTaskDataForInvoice(undefined);
        showSnackbar('Rechnung erfolgreich erstellt', 'success');
    };

    const handleChangeEmitter = () => {
        setInvoiceCreateOpen(false);
        setEmitterSelectOpen(true);
    };

    const handleSendEmail = async (recipients: string[], subject: string, message: string) => {
        if (!documentToEmail) return;

        try {
            const response = await fetch(
                `${
                    import.meta.env.DEV
                        ? 'http://localhost:8008'
                        : import.meta.env.VITE_BACKEND_URL || ''
                }/task/${task?.id}/documents/${documentToEmail.id}/email`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    },
                    body: JSON.stringify({
                        recipients: recipients,
                        subject: subject,
                        message: message
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            showSnackbar(
                `E-Mail erfolgreich versendet an: ${recipients.join(', ')}`,
                'success'
            );

            setOpenEmailModal(false);
            setDocumentToEmail(null);

        } catch (error) {
            console.error('Error sending email:', error);
            showSnackbar(
                `Fehler beim E-Mail-Versand: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
                'error'
            );
            throw error;
        }
    };

    const handleClose = async () => {
        if (hasUnsavedChanges) {
            setOpenUnsavedChangesModal(true);
            return;
        }

        if (task) {
            const showModal = await checkAndUpdateStatusToInProgressWithTask(task);

            if (!showModal) {
                performClose();
            }
        } else {
            performClose();
        }
    };

    const handleUnsavedChangesConfirm = async () => {
        setOpenUnsavedChangesModal(false);

        if (task) {
            const showModal = await checkAndUpdateStatusToInProgressWithTask(task);

            if (!showModal) {
                performClose();
            }
        } else {
            performClose();
        }
    };

    const handleUnsavedChangesCancel = () => {
        setOpenUnsavedChangesModal(false);
    };

    const getCurrentValue = (field: string) => {
        if (editMode && editValues.hasOwnProperty(field)) {
            return editValues[field];
        }
        if (pendingChanges.hasOwnProperty(field)) {
            return pendingChanges[field];
        }
        return task?.[field as keyof typeof task];
    };

    const getAssignedUserName = () => {
        const currentAssignedTo = getCurrentValue('assigned_to');
        if (!currentAssignedTo) return 'Nicht zugewiesen';
        const user = users.find(u => u.user_id === currentAssignedTo);
        if (!user) return getCurrentValue('assigned_to_name');
        return user?.fullname || user?.email || 'Unbekannter Benutzer';
    };

    const getBauvorhabenName = () => {
        const currentBauvorhabenId = getCurrentValue('bauvorhaben_id');
        if (!currentBauvorhabenId) {
            const projektName = getCurrentValue('projekt_name');
            return projektName || 'Kein Bauvorhaben';
        }
        const bvh = bau.find(b => b.id === currentBauvorhabenId);
        return bvh?.bvh_name || 'Unbekanntes Bauvorhaben';
    };

    const handleFieldUpdate = async (field: string, value: any) => {
        if (editMode) {
            setPendingChanges((prev) => ({...prev, [field]: value}));
            setEditValues((prev) => ({...prev, [field]: value}));
            setHasUnsavedChanges(true);
            return;
        }

        if (!task) {
            showSnackbar('Keine Aufgabe verfügbar', 'error');
            return;
        }

        const updateData: TaskUpdate = {[field]: value};

        call_backend_api(
            TasksService.updateTaskTaskTaskIdPut(taskId, updateData),
            (updatedTask: Task) => {
                const taskWithDefaults = {
                    ...updatedTask,
                    rapport_allow: updatedTask.rapport_allow ?? false,
                    images: task.images,
                    documents: task.documents
                };
                setTask(taskWithDefaults);
                showSnackbar(`${field} erfolgreich aktualisiert`);
                if (onTaskUpdated) onTaskUpdated();
            },
            undefined,
            undefined,
            true,
            'Aufgabe erfolgreich aktualisiert',
            'Fehler beim Speichern der Änderung'
        );
    };

    const handleFieldChange = (field: string, value: any) => {
        setPendingChanges((prev) => ({...prev, [field]: value}));
        setHasUnsavedChanges(true);
    };

    const handleTitleSave = () => {
        const newTitle = editValues.title;
        if (newTitle !== undefined && newTitle !== task?.title) {
            if (editMode) {
                handleFieldChange('title', newTitle);
            } else {
                handleFieldUpdate('title', newTitle);
            }
        }
        setEditingField(null);
        setEditValues({});
    };

    const handleDescriptionSave = () => {
        const newDescription = editValues.description;
        if (newDescription !== undefined && newDescription !== task?.description) {
            if (editMode) {
                handleFieldChange('description', newDescription);
            } else {
                handleFieldUpdate('description', newDescription);
            }
        }
        setEditingField(null);
        setEditValues({});
    };

    const handleRapportSave = () => {
        const newRapport = editValues.rapport;
        if (newRapport !== undefined && newRapport !== task?.rapport) {
            if (editMode) {
                handleFieldChange('rapport', newRapport);
            } else {
                handleFieldUpdate('rapport', newRapport);
            }
        }
        setEditingField(null);
        setEditValues({});
    };

    const handleKeyDown = (e: React.KeyboardEvent, saveFunction: () => void) => {
        if (e.key === 'Enter') {
            saveFunction();
        } else if (e.key === 'Escape') {
            setEditingField(null);
            setEditValues({});
        }
    };

    const handleStatusChange = (status: TaskStatus) => {
        setPendingChanges((prev) => ({...prev, status: status}));
        setHasUnsavedChanges(true);
        setEditMode(true);
        setStatusMenuAnchor(null);
    };

    const handleAssignmentChange = (userId: string) => {
        setPendingChanges((prev) => ({...prev, assigned_to: userId}));
        setHasUnsavedChanges(true);
        setEditMode(true);
        setAssignmentMenuAnchor(null);
    };

    const handleBauvorhabenChange = (bauvorhabenId: number | undefined) => {
        setPendingChanges((prev) => ({...prev, bauvorhaben_id: bauvorhabenId}));
        setHasUnsavedChanges(true);
        setEditMode(true);
        setBauvorhabenMenuAnchor(null);
    };

    const handleEditValueChange = (field: string, value: any) => {
        setEditValues(prev => ({...prev, [field]: value}));
        const originalValue = task?.[field as keyof typeof task];
        const hasChanged = value !== originalValue;
        if (hasChanged) {
            setHasUnsavedChanges(true);
        } else {
            const otherFields = Object.keys(editValues).filter(f => f !== field);
            const hasOtherChanges = otherFields.some(f => editValues[f] !== task?.[f as keyof typeof task]);
            setHasUnsavedChanges(hasOtherChanges);
        }
    };

    const handleStartEdit = () => {
        setEditMode(true);
        setEditingField(null);
        setEditValues({
            description: task?.description ?? '',
            rapport: task?.rapport ?? '',
            projekt_name: task?.projekt_name ?? '',
            // Auftraggeber
            auftraggeber_name: task?.auftraggeber_name ?? '',
            auftraggeber_email: task?.auftraggeber_email ?? '',
            auftraggeber_phone: task?.auftraggeber_phone ?? '',
            auftraggeber_address: task?.auftraggeber_address ?? '',
            auftraggeber_street: task?.auftraggeber_street ?? '',
            auftraggeber_additional_street: task?.auftraggeber_additional_street ?? '',
            auftraggeber_postal_code: task?.auftraggeber_postal_code ?? '',
            auftraggeber_city: task?.auftraggeber_city ?? '',
            // Terminvereinbarung
            appointment_name: task?.appointment_name ?? '',
            appointment_email: task?.appointment_email ?? '',
            appointment_phone: task?.appointment_phone ?? '',
            appointment_street: task?.appointment_street ?? '',
            appointment_additional_street: task?.appointment_additional_street ?? '',
            appointment_postal_code: task?.appointment_postal_code ?? '',
            appointment_city: task?.appointment_city ?? '',
            // Rechnungsempfänger
            invoice_name: task?.invoice_name ?? '',
            invoice_email: task?.invoice_email ?? '',
            invoice_phone: task?.invoice_phone ?? '',
            invoice_street: task?.invoice_street ?? '',
            invoice_additional_street: task?.invoice_additional_street ?? '',
            invoice_postal_code: task?.invoice_postal_code ?? '',
            invoice_city: task?.invoice_city ?? '',
        });
    };

    const handleCancelEdit = () => {
        setPendingChanges({});
        setHasUnsavedChanges(false);
        setEditMode(false);
        setEditingField(null);
        setEditValues({});
    };

    const handleSaveChanges = async () => {
        if (!task || !hasUnsavedChanges) return;

        setSubmitting(true);

        try {
            const updateData: ExtendedTaskUpdate = {};

            if (editValues.description !== undefined && editValues.description !== task.description) {
                updateData.description = editValues.description;
            }
            if (editValues.rapport !== undefined && editValues.rapport !== task.rapport) {
                updateData.rapport = editValues.rapport;
            }
            if (editValues.title !== undefined && editValues.title !== task.title) {
                updateData.title = editValues.title;
            }
            if (editValues.projekt_name !== undefined && editValues.projekt_name !== task.projekt_name) {
                updateData.projekt_name = editValues.projekt_name;
            }

            // Handle Auftraggeber fields
            if (editValues.auftraggeber_name !== undefined && editValues.auftraggeber_name !== task.auftraggeber_name) {
                updateData.auftraggeber_name = editValues.auftraggeber_name;
            }
            if (editValues.auftraggeber_email !== undefined && editValues.auftraggeber_email !== task.auftraggeber_email) {
                updateData.auftraggeber_email = editValues.auftraggeber_email;
            }
            if (editValues.auftraggeber_phone !== undefined && editValues.auftraggeber_phone !== task.auftraggeber_phone) {
                updateData.auftraggeber_phone = editValues.auftraggeber_phone;
            }
            if (editValues.auftraggeber_address !== undefined && editValues.auftraggeber_address !== task.auftraggeber_address) {
                updateData.auftraggeber_address = editValues.auftraggeber_address;
            }
            if (editValues.auftraggeber_street !== undefined && editValues.auftraggeber_street !== task.auftraggeber_street) {
                updateData.auftraggeber_street = editValues.auftraggeber_street;
            }
            if (editValues.auftraggeber_additional_street !== undefined && editValues.auftraggeber_additional_street !== task.auftraggeber_additional_street) {
                updateData.auftraggeber_additional_street = editValues.auftraggeber_additional_street;
            }
            if (editValues.auftraggeber_postal_code !== undefined && editValues.auftraggeber_postal_code !== task.auftraggeber_postal_code) {
                updateData.auftraggeber_postal_code = editValues.auftraggeber_postal_code;
            }
            if (editValues.auftraggeber_city !== undefined && editValues.auftraggeber_city !== task.auftraggeber_city) {
                updateData.auftraggeber_city = editValues.auftraggeber_city;
            }

            // Handle Terminvereinbarung (appointment) fields
            if (editValues.appointment_name !== undefined && editValues.appointment_name !== task.appointment_name) {
                updateData.appointment_name = editValues.appointment_name;
            }
            if (editValues.appointment_email !== undefined && editValues.appointment_email !== task.appointment_email) {
                updateData.appointment_email = editValues.appointment_email;
            }
            if (editValues.appointment_phone !== undefined && editValues.appointment_phone !== task.appointment_phone) {
                updateData.appointment_phone = editValues.appointment_phone;
            }
            if (editValues.appointment_street !== undefined && editValues.appointment_street !== task.appointment_street) {
                updateData.appointment_street = editValues.appointment_street;
            }
            if (editValues.appointment_additional_street !== undefined && editValues.appointment_additional_street !== task.appointment_additional_street) {
                updateData.appointment_additional_street = editValues.appointment_additional_street;
            }
            if (editValues.appointment_postal_code !== undefined && editValues.appointment_postal_code !== task.appointment_postal_code) {
                updateData.appointment_postal_code = editValues.appointment_postal_code;
            }
            if (editValues.appointment_city !== undefined && editValues.appointment_city !== task.appointment_city) {
                updateData.appointment_city = editValues.appointment_city;
            }

            // Handle Rechnungsempfänger (invoice) fields
            if (editValues.invoice_name !== undefined && editValues.invoice_name !== task.invoice_name) {
                updateData.invoice_name = editValues.invoice_name;
            }
            if (editValues.invoice_email !== undefined && editValues.invoice_email !== task.invoice_email) {
                updateData.invoice_email = editValues.invoice_email;
            }
            if (editValues.invoice_phone !== undefined && editValues.invoice_phone !== task.invoice_phone) {
                updateData.invoice_phone = editValues.invoice_phone;
            }
            if (editValues.invoice_street !== undefined && editValues.invoice_street !== task.invoice_street) {
                updateData.invoice_street = editValues.invoice_street;
            }
            if (editValues.invoice_additional_street !== undefined && editValues.invoice_additional_street !== task.invoice_additional_street) {
                updateData.invoice_additional_street = editValues.invoice_additional_street;
            }
            if (editValues.invoice_postal_code !== undefined && editValues.invoice_postal_code !== task.invoice_postal_code) {
                updateData.invoice_postal_code = editValues.invoice_postal_code;
            }
            if (editValues.invoice_city !== undefined && editValues.invoice_city !== task.invoice_city) {
                updateData.invoice_city = editValues.invoice_city;
            }

            Object.keys(pendingChanges).forEach(field => {
                const newValue = pendingChanges[field];
                const originalValue = task[field as keyof typeof task];

                if (newValue !== originalValue) {
                    updateData[field as keyof TaskUpdate] = newValue;
                }
            });

            if (Object.keys(updateData).length === 0) {
                showSnackbar('Keine Änderungen zum Speichern', 'info');
                setSubmitting(false);
                return;
            }

            call_backend_api(
                TasksService.updateTaskTaskTaskIdPut(taskId, updateData),
                (updatedTask: Task) => {
                    const taskWithDefaults = {
                        ...updatedTask,
                        rapport_allow: updatedTask.rapport_allow ?? false,
                        images: task.images,
                        documents: task.documents
                    };
                    setTask(taskWithDefaults);
                    setPendingChanges({});
                    setHasUnsavedChanges(false);
                    setEditMode(false);
                    setEditingField(null);
                    setEditValues({});
                    setSubmitting(false);
                    showSnackbar('Aufgabe erfolgreich gespeichert', 'success');
                    if (onTaskUpdated) onTaskUpdated();
                },
                (error: any) => {
                    setSubmitting(false);
                    console.error('Error saving changes:', error);
                    showSnackbar('Fehler beim Speichern der Änderungen', 'error');
                },
                undefined,
                false
            );
        } catch (error) {
            setSubmitting(false);
            console.error('Error in handleSaveChanges:', error);
            showSnackbar('Fehler beim Speichern der Änderungen', 'error');
        }
    };

    const handleCompleteTask = () => {
        if (task?.rapport_allow) {
            setOpenRapportComplete(true);
        } else {
            setOpenEndTask(true);
        }
    };

    const closeEndTask = async () => {
        setOpenEndTask(false);

        try {
            if (task?.status === TaskStatus.IN_PROGRESS ||
                task?.status === TaskStatus.BLOCKED ||
                task?.status === TaskStatus.OPEN) {
                call_backend_api(
                    TasksService.updateTaskTaskTaskIdPut(taskId, {
                        status: TaskStatus.COMPLETED,
                    }),
                    (updatedTask: Task) => {
                        setTask({
                            ...updatedTask,
                            images: task?.images || [],
                            documents: task?.documents || [],
                        });
                        showSnackbar('Aufgabe erfolgreich abgeschlossen', 'success');
                        if (onTaskUpdated) onTaskUpdated();
                        onClose();
                    },
                    (error: any) => {
                        console.error('Error completing task:', error);
                        showSnackbar('Fehler beim Abschließen der Aufgabe', 'error');
                    },
                    undefined,
                    false
                );
            } else {
                if (onTaskUpdated) onTaskUpdated();
                onClose();
            }
        } catch (error) {
            console.error('Error completing task:', error);
            showSnackbar('Fehler beim Abschließen der Aufgabe', 'error');
        }
    };

    const handleRapportComplete = async (rapportData: {
        rapportText: string;
        selectedTaskImages: TaskImage[];
        uploadedImages: File[];
        signature: string | null;
        anfahrt_abfahrt_mit_kfz: boolean;
        material: string;
        arbeitszeit: number;
        anzahl_monteure: number;
    }) => {
        setOpenRapportComplete(false);

        try {
            // Step 1: First save rapport data WITHOUT completing the task
            // This allows us to upload the PDF before the completion email is sent
            await call_backend_api(
                TasksService.updateTaskTaskTaskIdPut(taskId, {
                    rapport: rapportData.rapportText,
                    anfahrt_abfahrt_mit_kfz: rapportData.anfahrt_abfahrt_mit_kfz,
                    material: rapportData.material,
                    arbeitszeit: rapportData.arbeitszeit,
                    anzahl_monteure: rapportData.anzahl_monteure,
                    // Don't set status to COMPLETED yet - do that after PDF upload
                }),
                (updatedTask: Task) => {
                    setTask({
                        ...updatedTask,
                        images: task?.images || [],
                        documents: task?.documents || [],
                    });
                    console.log('Rapport data saved successfully');
                },
                (error: any) => {
                    console.error('Error saving rapport data:', error);
                    showSnackbar('Fehler beim Speichern des Rapports', 'error');
                    throw error;
                },
                undefined,
                false
            );

            // Step 2: Upload images if any
            if (rapportData.uploadedImages.length > 0) {
                try {
                    const imageCount = rapportData.uploadedImages.length;
                    console.log('Uploading new images to task:', imageCount);

                    // Show loading toast
                    showSnackbar(
                        `Lade ${imageCount} ${imageCount === 1 ? 'Bild' : 'Bilder'} hoch...`,
                        'info',
                        true
                    );

                    // Upload all images in parallel and wait for all to complete
                    const uploadPromises = rapportData.uploadedImages.map((file) =>
                        call_backend_api(
                            TasksService.uploadTaskImageTaskTaskIdImagesPost(taskId, {file}),
                            () => {
                                console.log('Image uploaded successfully:', file.name);
                            },
                            (error: any) => {
                                console.error('Error uploading image:', file.name, error);
                                throw new Error(`Fehler beim Hochladen von ${file.name}`);
                            },
                            undefined,
                            false
                        )
                    );

                    await Promise.all(uploadPromises);

                    console.log('All images uploaded successfully');
                    showSnackbar(
                        `${imageCount} ${imageCount === 1 ? 'Bild' : 'Bilder'} erfolgreich hochgeladen`,
                        'success'
                    );
                } catch (error) {
                    console.error('Error uploading images:', error);
                    showSnackbar('Fehler beim Hochladen der Bilder', 'error');
                    throw error; // Re-throw to stop rapport completion
                }
            }

            // Step 3: Generate PDF
            const pdfResult = await createRapport({
                taskId,
                task: {
                    ...task!,
                    rapport: rapportData.rapportText,
                    status: TaskStatus.COMPLETED // Status für PDF als abgeschlossen setzen
                },
                users,
                bau,
                formatDate,
                getCurrentTimeFrom,
                getCurrentTimeTo,
                getTotalDuration,
                getStatusLabel,
                getRapportText: () => rapportData.rapportText,
                showSnackbar,
                setOpenEndTask,
                selectedImages: rapportData.selectedTaskImages.map(img => img.id),
                signature: rapportData.signature,
                uploadedFiles: rapportData.uploadedImages,
                anfahrt_abfahrt_mit_kfz: rapportData.anfahrt_abfahrt_mit_kfz,
                material: rapportData.material,
                arbeitszeit: rapportData.arbeitszeit,
                anzahl_monteure: rapportData.anzahl_monteure,
            });

            // Step 4: Upload PDF first (before marking task as completed)
            if (pdfResult) {
                try {
                    console.log('Uploading generated PDF to task');
                    const pdfFile = new File([pdfResult.pdfBlob], pdfResult.fileName, {type: 'application/pdf'});

                    await call_backend_api(
                        TaskServiceUtils.uploadDocumentWithType(taskId, pdfFile, 'RAPPORT'),
                        (response: any) => {
                            console.log('PDF uploaded successfully as document');
                            showSnackbar('PDF-Rapport erfolgreich hochgeladen', 'success');

                            call_backend_api(
                                TasksService.updateTaskTaskTaskIdPut(taskId, {
                                    status: TaskStatus.COMPLETED,
                                }),
                                (updatedTask: Task) => {
                                    setTask({
                                        ...updatedTask,
                                        images: task?.images || [],
                                        documents: task?.documents || [],
                                    });
                                    console.log('Task marked as completed - email will be sent with PDF');
                                },
                                (error: any) => {
                                    console.error('Error completing task:', error);
                                    showSnackbar('Fehler beim Abschließen der Aufgabe', 'error');
                                },
                                undefined,
                                false
                            );

                            showSnackbar('Rapport gespeichert und Aufgabe erfolgreich abgeschlossen', 'success');
                            if (onTaskUpdated) onTaskUpdated();
                            onClose();

                        },
                        (error: any) => {
                            console.error('Error uploading PDF:', error);
                            showSnackbar('Fehler beim Hochladen des PDF-Rapports', 'error');
                        },
                        undefined,
                        false
                    );

                } catch (error) {
                    console.error('Error uploading PDF document:', error);
                    showSnackbar('Fehler beim Verarbeiten des PDF-Dokuments', 'error');
                }
            }
            console.log('#######FINAL STEP######')

        } catch (error) {
            console.error('Error processing rapport:', error);
            showSnackbar('Fehler beim Speichern des Rapports', 'error');
        }
    };

    const handleRemoveTask = (taskId: number) => {
        const isCurrentUserCreator = task?.created_by === sessionUserId;
        const isCurrentUserAdmin = user_roll?.includes('Administrator');

        if (!isCurrentUserAdmin && !isCurrentUserCreator) {
            const errorMsg =
                'ZUGRIFF VERWEIGERT: Sie haben keine Berechtigung, diese Aufgabe zu archivieren. Nur der Ersteller der Aufgabe oder Administratoren können Tasks archivieren.';
            showSnackbar(errorMsg, 'error');
            return;
        }

        if (task?.created_by !== sessionUserId && !isCurrentUserAdmin) {
            const errorMsg =
                'SICHERHEITSFEHLER: Archivierung verhindert - Sie sind nicht der Ersteller dieser Aufgabe.';
            console.error('Security check failed before API call');
            showSnackbar(errorMsg, 'error');
            return;
        }

        setArchiving(true);
        call_backend_api(
            TasksService.deleteTaskTaskTaskIdDelete(taskId),
            () => {
                setArchiving(false);
                setOpenDeleteTask(false);
                showSnackbar('Aufgabe erfolgreich archiviert', 'success');
                onClose();
                if (onTaskUpdated) onTaskUpdated();
            },
            (apiError: any) => {
                setArchiving(false);
                console.error('API Error:', apiError);
                if (apiError?.status === 403) {
                    showSnackbar('Sie haben keine Berechtigung, diese Aufgabe zu archivieren.', 'error');
                } else {
                    showSnackbar(
                        `Fehler beim Archivieren der Aufgabe: ${apiError?.message || 'Unbekannter Fehler'}`,
                        'error'
                    );
                }
            },
            undefined,
            false
        );
    };

    const handleRestoreTask = async (taskId: number) => {
        try {
            call_backend_api(
                TasksService.restoreTaskTaskTaskIdRestorePost(taskId, {
                    new_status: TaskStatus.OPEN,
                }),
                async () => {
                    call_backend_api(
                        TasksService.getTaskTaskTaskIdGet(taskId),
                        (taskData: Task) => {
                            setTask({
                                ...taskData,
                                images: task?.images || [],
                                documents: task?.documents || [],
                            });
                            showSnackbar('Aufgabe erfolgreich wiederhergestellt', 'success');
                            if (onTaskUpdated) onTaskUpdated();
                        },
                        (error: any) => {
                            console.error('Error refreshing task after restore:', error);
                            showSnackbar('Fehler beim Aktualisieren der Aufgabe nach Wiederherstellung', 'error');
                        },
                        undefined,
                        false
                    );
                },
                (error: any) => {
                    console.error('Error restoring task:', error);
                    showSnackbar('Fehler beim Wiederherstellen der Aufgabe', 'error');
                },
                undefined,
                false
            );
        } catch (err) {
            console.error('Error restoring task:', err);
            showSnackbar('Fehler beim Wiederherstellen der Aufgabe', 'error');
        }
    };

    const handlePermanentDeleteFile = (item: TaskImage | TaskDocument) => {
        const fileType = 'image_path' in item ? 'image' : 'document';
        setFileToDelete({
            id: item.id,
            name: fileType === 'image' ? `Bild ${item.id}` : (item as TaskDocument).filename,
            type: fileType,
            item: item
        });
        setOpenPermanentDeleteFile(true);
    };

    const confirmPermanentDeleteFile = async () => {
        if (!fileToDelete) return;

        try {
            if (fileToDelete.type === 'image') {
                showSnackbar('Permanente Bildlöschung noch nicht im Backend implementiert', 'warning');
            } else if (fileToDelete.type === 'document') {
                showSnackbar('Permanente Dokumentlöschung noch nicht im Backend implementiert', 'warning');
            }

            setOpenPermanentDeleteFile(false);
            setFileToDelete(null);

        } catch (error) {
            console.error('Error permanently deleting file:', error);
            showSnackbar('Fehler beim permanenten Löschen der Datei', 'error');
        }
    };
    const handlePermanentDeleteTask = async (taskId: number) => {
        const isCurrentUserCreator = task?.created_by === sessionUserId;
        const isCurrentUserAdmin = user_roll?.includes('Administrator');

        if (!isCurrentUserAdmin && !isCurrentUserCreator) {
            const errorMsg =
                'ZUGRIFF VERWEIGERT: Sie haben keine Berechtigung, diese Aufgabe permanent zu löschen. Nur der Ersteller der Aufgabe oder Administratoren können Tasks permanent löschen.';
            console.error('Permanent deletion attempt blocked:', {
                reason: 'Not creator and not admin',
                sessionUserId,
                taskCreatedBy: task?.created_by,
                isAdmin: isCurrentUserAdmin,
                isCreator: isCurrentUserCreator,
            });
            showSnackbar(errorMsg, 'error');
            return;
        }

        if (task?.created_by !== sessionUserId && !isCurrentUserAdmin) {
            const errorMsg =
                'SICHERHEITSFEHLER: Permanente Löschung verhindert - Sie sind nicht der Ersteller dieser Aufgabe.';
            console.error('Security check failed before permanent delete API call');
            showSnackbar(errorMsg, 'error');
            return;
        }

        if (task?.status !== TaskStatus.ARCHIVED) {
            showSnackbar('Nur archivierte Aufgaben können permanent gelöscht werden.', 'warning');
            return;
        }

        const confirmDelete = window.confirm(
            'Sind Sie sicher, dass Sie diese Aufgabe permanent löschen möchten? ' +
            'Diese Aktion kann nicht rückgängig gemacht werden!'
        );

        if (!confirmDelete) return;

        setPermanentDeleting(true);
        call_backend_api(
            TasksService.permanentlyDeleteTaskTaskTaskIdPermanentDelete(taskId),
            () => {
                setPermanentDeleting(false);
                setOpenPermanentDeleteTask(false);
                showSnackbar('Aufgabe permanent gelöscht', 'success');
                onClose();
                if (onTaskUpdated) onTaskUpdated();
            },
            (apiError: any) => {
                setPermanentDeleting(false);
                console.error('API Error:', apiError);
                if (apiError?.status === 403) {
                    showSnackbar(
                        'Sie haben keine Berechtigung, diese Aufgabe permanent zu löschen.',
                        'error'
                    );
                } else if (apiError?.status === 400) {
                    showSnackbar('Nur archivierte Aufgaben können permanent gelöscht werden.', 'error');
                } else {
                    showSnackbar(
                        `Fehler beim permanenten Löschen der Aufgabe: ${
                            apiError?.message || 'Unbekannter Fehler'
                        }`,
                        'error'
                    );
                }
            },
            undefined,
            false
        );
    };

    const apiHandlers = createTaskApiHandlers({
        call_backend_api,
        taskId,
        task,
        setTask,
        setUploading,
        fileInputRef,
        onTaskUpdated,
        showSnackbar,
    });

    const taskHandlers = {
        handleSubmit: async () => {
            if (!task || Object.keys(pendingChanges).length === 0) return;
            setSubmitting(true);
            try {
                const updatedTask = {...task, ...pendingChanges};

                call_backend_api(
                    TasksService.updateTaskTaskTaskIdPut(taskId, updatedTask),
                    (response: Task) => {
                        setTask({...response, images: task.images, documents: task.documents});
                        setPendingChanges({});
                        setHasUnsavedChanges(false);
                        setEditMode(false);
                        showSnackbar('Aufgabe erfolgreich aktualisiert');
                        if (onTaskUpdated) onTaskUpdated();
                    },
                    (error: any) => {
                        console.error('Error updating task:', error);
                        showSnackbar('Fehler beim Aktualisieren der Aufgabe', 'error');
                    },
                    () => {
                        setSubmitting(false);
                    },
                    true,
                    'Aufgabe erfolgreich aktualisiert',
                    'Fehler beim Aktualisieren der Aufgabe'
                );
            } catch (error) {
                console.error('Error updating task:', error);
                showSnackbar('Fehler beim Aktualisieren der Aufgabe', 'error');
                setSubmitting(false);
            }
        },
        handleCancel: handleCancelEdit,
        handleStatusChange: (status: TaskStatus) => {
            handleStatusChange(status);
        },
    };

    if (!isOpen) return null;

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                maxWidth='lg'
                fullWidth
                PaperProps={{
                    sx: {
                        height: 'fit-content',
                        maxHeight: '90vh',
                        borderRadius: 3,
                        boxShadow: '0 16px 48px rgba(0,0,0,0.1)',
                        backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                    },
                }}>
                <DialogTitle sx={{
                    px: 3,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                }}>
                    <TaskHeader
                        task={task}
                        taskId={taskId}
                        editMode={editMode}
                        editingField={editingField}
                        editValues={editValues}
                        hasUnsavedChanges={hasUnsavedChanges}
                        isTaskArchived={isTaskArchived}
                        submitting={submitting}
                        getCurrentValue={getCurrentValue}
                        handleClose={handleClose}
                        handleStartEdit={handleStartEdit}
                        handleSave={handleSaveChanges}
                        handleCancelEdit={handleCancelEdit}
                        setEditingField={setEditingField}
                        setEditValues={setEditValues}
                        handleTitleSave={handleTitleSave}
                        handleKeyDown={handleKeyDown}
                        setOpenEndTask={setOpenEndTask}
                        handleArchiveTask={() => setOpenDeleteTask(true)}
                        handleRestoreTask={() => handleRestoreTask(taskId)}
                        canArchive={isTaskCreator || isAdmin}
                        sessionUserId={sessionUserId}
                    />
                </DialogTitle>

                <DialogContent sx={{
                    px: 3,
                    py: 2,
                    backgroundColor: '#fafafa',
                    backgroundImage: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
                }}>
                    {error && (
                        <Alert severity='error' sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}
                    {loading ? (
                        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                            <CircularProgress size={60}/>
                        </Box>
                    ) : task ? (
                        <Box>
                            <Box sx={{mb: 3}}>
                                <TaskTitle
                                    task={task}
                                    editMode={editMode}
                                    editingField={editingField}
                                    editValues={editValues}
                                    hasUnsavedChanges={hasUnsavedChanges}
                                    isTaskArchived={isTaskArchived}
                                    getCurrentValue={getCurrentValue}
                                    setEditingField={setEditingField}
                                    setEditValues={setEditValues}
                                    handleTitleSave={handleTitleSave}
                                    handleKeyDown={handleKeyDown}
                                />
                            </Box>

                            <TaskStatusDetails
                                task={task}
                                editMode={editMode}
                                isTaskArchived={isTaskArchived}
                                isAdmin={!!isAdmin}
                                getCurrentValue={getCurrentValue}
                                getStatusIcon={getStatusIcon}
                                getStatusLabel={getStatusLabel}
                                getStatusColor={getStatusColor}
                                getAssignedUserName={getAssignedUserName}
                                getBauvorhabenName={getBauvorhabenName}
                                formatDate={formatDate}
                                statusMenuAnchor={statusMenuAnchor}
                                setStatusMenuAnchor={setStatusMenuAnchor}
                                assignmentMenuAnchor={assignmentMenuAnchor}
                                setAssignmentMenuAnchor={setAssignmentMenuAnchor}
                                bauvorhabenMenuAnchor={bauvorhabenMenuAnchor}
                                setBauvorhabenMenuAnchor={setBauvorhabenMenuAnchor}
                                handleStatusChange={handleStatusChange}
                                handleAssignmentChange={handleAssignmentChange}
                                handleBauvorhabenChange={handleBauvorhabenChange}
                                handleFieldUpdate={handleFieldUpdate}
                                users={users}
                                bau={bau}
                            />

                            <TaskContacts
                                task={task}
                                editMode={editMode}
                                isTaskArchived={isTaskArchived}
                                getCurrentValue={getCurrentValue}
                                handleFieldUpdate={handleFieldUpdate}
                                contacts={contacts}
                            />

                            <TimeTracking
                                editMode={editMode}
                                getCurrentTimeFrom={getCurrentTimeFrom}
                                getCurrentTimeTo={getCurrentTimeTo}
                                getTotalDuration={getTotalDuration}
                                setWtFrom={setWtFrom}
                                setWtTo={setWtTo}
                                handleFieldUpdate={handleFieldUpdate}
                            />

                            <TaskDescription
                                task={task}
                                editMode={editMode}
                                editingField={editingField}
                                editValues={editValues}
                                getCurrentValue={getCurrentValue}
                                setEditingField={setEditingField}
                                handleEditValueChange={handleEditValueChange}
                                handleDescriptionSave={handleDescriptionSave}
                            />

                            <TaskAttachments
                                task={task}
                                uploading={uploading}
                                fileInputRef={fileInputRef}
                                formatDate={formatDate}
                                getDocumentIcon={getDocumentIcon}
                                handleFileUpload={handleFileUpload}
                                handleImageClick={handleImageClick}
                                handleEditImage={handleEditImage}
                                handleCropImage={handleCropImage}
                                handleDownloadImage={handleDownloadImage}
                                handleDeleteImage={handleDeleteImage}
                                handleDownloadDocument={handleDownloadDocument}
                                handleShareDocument={handleShareDocument}
                                handleDeleteDocument={handleDeleteDocument}
                                userRole={user_roll}
                                onCreateInvoice={handleCreateInvoiceFromRapport}
                            />
                        </Box>
                    ) : (
                        <Alert severity='error'>Aufgabe nicht gefunden</Alert>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        justifyContent: 'flex-end',
                        gap: 1,
                        flex: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        onClick={handleClose}
                        variant='outlined'
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 4,
                            color: 'text.secondary',
                            borderColor: 'divider',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        {task?.status === TaskStatus.COMPLETED ? 'Schließen' : 'Später beenden'}
                    </Button>

                    {/* Aufgabe beenden - nur für aktive Tasks */}
                    {!isTaskArchived &&
                        task?.status !== TaskStatus.COMPLETED &&
                        task?.status !== TaskStatus.ARCHIVED && (
                            <Button
                                onClick={handleCompleteTask}
                                variant='contained'
                                color='success'
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    px: 4,
                                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                                    '&:hover': {
                                        boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                                    }
                                }}
                            >
                                Aufgabe beenden
                            </Button>
                        )}
                </DialogActions>
            </Dialog>

            {/* Image Crop Dialog */}
            {croppingImageFile && (
                <ImageCrop
                    imageFile={croppingImageFile}
                    onSave={handleSaveCroppedImage}
                    onCancel={() => {
                        setCroppingImageId(null);
                        setCroppingImageFile(null);
                    }}
                    isOpen={croppingImageId !== null}
                />
            )}

            {/* Modals */}
            <ImagePreviewModal
                open={Boolean(selectedImage)}
                onClose={() => setSelectedImage(null)}
                selectedImage={selectedImage}
            />

            <ImageEditModal
                open={Boolean(editingImageFile)}
                onClose={() => {
                    setEditingImageId(null);
                    setEditingImageFile(null);
                }}
                editingImageFile={editingImageFile}
                onSave={handleSaveEditedImage}
            />

            <ArchiveTaskModal
                open={openDeleteTask}
                onClose={() => setOpenDeleteTask(false)}
                onConfirm={() => {
                    handleRemoveTask(taskId);
                }}
                loading={archiving}
            />

            <PermanentDeleteModal
                open={openPermanentDeleteTask}
                onClose={() => setOpenPermanentDeleteTask(false)}
                onConfirm={() => {
                    handlePermanentDeleteTask(taskId);
                }}
                loading={permanentDeleting}
            />

            <StatusChangeModal
                open={openStatusChangeModal}
                onClose={handleStatusChangeCancel}
                onConfirm={handleStatusChangeConfirm}
            />

            <UnsavedChangesModal
                open={openUnsavedChangesModal}
                onClose={handleUnsavedChangesCancel}
                onConfirm={handleUnsavedChangesConfirm}
            />

            <CompleteTaskModal
                open={openEndTask}
                onClose={() => setOpenEndTask(false)}
                onConfirm={closeEndTask}
            />

            <RapportCompleteModal
                open={openRapportComplete}
                onClose={() => setOpenRapportComplete(false)}
                onConfirm={handleRapportComplete}
                taskTitle={task?.title}
                initialRapportText={task?.rapport || ''}
                taskImages={task?.images || []}
            />

            <PermanentDeleteModalFile
                open={openDeleteFileModal}
                onClose={() => {
                    setOpenDeleteFileModal(false);
                    setFileToDelete(null);
                }}
                onConfirm={confirmDeleteFile}
                fileToDelete={fileToDelete}
            />

            <PermanentDeleteModalFile
                open={openPermanentDeleteFile}
                onClose={() => {
                    setOpenPermanentDeleteFile(false);
                    setFileToDelete(null);
                }}
                onConfirm={confirmPermanentDeleteFile}
                fileToDelete={fileToDelete}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={snackbarLoading ? null : 6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}>
                <Alert
                    onClose={snackbarLoading ? undefined : handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{width: '100%'}}
                    icon={snackbarLoading ? <CircularProgress size={20} color="inherit"/> : undefined}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Invoice Creation Dialogs */}
            <EmitterSelectDialog
                open={emitterSelectOpen}
                onClose={() => {
                    setEmitterSelectOpen(false);
                    setSelectedRapportDocument(null);
                    setTaskDataForInvoice(undefined);
                }}
                onSelect={handleEmitterSelect}
            />

            {selectedEmitter && (
                <InvoiceCreateDialog
                    open={invoiceCreateOpen}
                    onClose={handleInvoiceClose}
                    onSave={handleInvoiceSave}
                    emitter={selectedEmitter}
                    onChangeEmitter={handleChangeEmitter}
                    taskId={taskId}
                    taskData={taskDataForInvoice}
                />
            )}

            {/* E-Mail Document Modal */}
            <EmailDocumentModal
                open={openEmailModal}
                onClose={() => {
                    setOpenEmailModal(false);
                    setDocumentToEmail(null);
                }}
                document={documentToEmail}
                task={task}
                onSend={handleSendEmail}
            />
        </>
    );
}
